import { Sites } from '@ps/platform-message-framework/common/Sites';
import { ResolverTag } from '@ps/web-core-enums';
import { CashierPath } from '@ps/web-pam-types';
import { StarsButton, Translation } from '@ps/web-shared-components';
import { translatedLink } from '@ps/web-shared-ui';
import React from 'react';
import { useSelector } from 'react-redux';
import { Route } from 'react-router';

import useOpenWindow from '@swf/core/hooks/useOpenWindow';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';

import { CashierFlowType } from '../../types/Cashier';

import {
  TXTCLI_Gambling_can_be_harmful_if_not_ELL,
  TXTCLI_Online_gambling_is_regulated_i_ELL,
  TXTCLI_Operated_by_TSG_Interactive_Ga_ELL,
  TXTCLI_PokerStars_is_committed_to_sup_ELL,
  TXTCLI_Privacy_Policy,
  TXTCLI_Stars_Interactive_Limited_is_l_ELL,
  TXTCLI_Use_our_online_tools_and_play__ELL,
  TXTMOB_Terms_of_Service_1,
  TXTWCL_Copyright_X_2001_2024_Rational_ELL,
  TXTWCL_Gaming_Account_NumberX_N0,
  TXTWCL_It_is_a_Federal_offense_for_pe_ELL,
  TXTWCL_Responsible_gaming,
  TXTWCL_TSG_Interactive_US_Services_Li_ELL,
  TXTWCL_Terms_of_Service,
  TXTWCL_XBet_with_your_headX_not_over__ELL
} from './RegulatoryInformation.messages.json';
import {
  italianLicense,
  regulatoryButton,
  textContainer,
  textContainerUs,
  wrapper
} from './RegulatoryInformation.scss';

enum Flows {
  Deposit = `/:locale?${CashierPath.Deposit}/`,
  Withdraw = `/:locale?${CashierPath.Withdraw}/`,
  TransactionHistory = `/:locale?${CashierPath.TransactionHistory}/`,
  ManageCurrency = `/:locale?${CashierPath.ManageCurrencies}/`,
  P2P = `/:locale?${CashierPath.P2pTransfer}/`,
  Cashier = `/:locale?${CashierPath.Cashier}/`,
  SignUp = `/:locale?${CashierPath.Signup}/`
}

const RegulatoryInformation: React.FC = () => {
  const site = useSelector(select.cms.getSiteId);
  const embedded = useSelector(select.device.isEmbedded);
  const gamingAccountNumber = useSelector(select.account.getGamingAccountNumber);
  const {
    data: { cashierFlow }
  } = useWizardContext();

  const depositOnRegistration = cashierFlow && cashierFlow === CashierFlowType.FirstDeposit;

  const comFlows = [
    Flows.Cashier,
    Flows.Deposit,
    Flows.Withdraw,
    Flows.TransactionHistory,
    Flows.P2P,
    Flows.ManageCurrency
  ] as string[];

  const ukAndIt = [Flows.Cashier, Flows.Deposit] as string[];

  if (depositOnRegistration) {
    comFlows.push(Flows.SignUp);
    ukAndIt.push(Flows.SignUp);
  }

  const resolverTag: Partial<Record<Sites, ResolverTag>> = {
    [Sites.UK]: ResolverTag.UKLicenseAccountNumber,
    [Sites.ITA]: ResolverTag.HelpResponsibleGaming,
    [Sites.COM]: ResolverTag.HelpResponsibleGaming,
    [Sites.EU]: ResolverTag.HelpResponsibleGaming
  } as const;

  const openWindow = useOpenWindow({
    resolverTag: resolverTag[site] ?? ResolverTag.HelpResponsibleGaming,
    openTab: true
  });

  const openMaltaTag = useOpenWindow({
    resolverTag: ResolverTag.HelpMalta,
    openTab: true
  });

  const openSettingLimits = useOpenWindow({
    resolverTag: ResolverTag.HelpSettingLimits,
    openTab: true
  });

  const openTOS = useOpenWindow({
    resolverTag: ResolverTag.HelpTOS,
    openTab: true
  });
  const openPrivacy = useOpenWindow({
    resolverTag: ResolverTag.MCPrivacy,
    openTab: true
  });
  let message = null;
  switch (site) {
    case Sites.UK:
      message = (
        <Route path={ukAndIt}>
          <Translation
            {...TXTCLI_Stars_Interactive_Limited_is_l_ELL}
            values={{ a: translatedLink({ onClick: openWindow }) }}
          />
        </Route>
      );
      break;
    case Sites.ITA:
      message = (
        <Route path={ukAndIt}>
          <div className={italianLicense}>
            <Translation
              tagName="div"
              {...TXTWCL_Gaming_Account_NumberX_N0}
              values={{ 0: gamingAccountNumber }}
            />{' '}
            <Translation
              tagName="div"
              {...TXTCLI_PokerStars_is_committed_to_sup_ELL}
              values={{ a: translatedLink({ onClick: openWindow }) }}
            />
          </div>
        </Route>
      );
      break;
    case Sites.COM:
      message = embedded && (
        <Route path={comFlows}>
          <div className={textContainer}>
            <Translation {...TXTCLI_Operated_by_TSG_Interactive_Ga_ELL} />{' '}
            <Translation
              {...TXTCLI_Online_gambling_is_regulated_i_ELL}
              values={{ a: translatedLink({ onClick: openMaltaTag }) }}
            />
          </div>
          <div className={textContainer}>
            <Translation {...TXTCLI_Gambling_can_be_harmful_if_not_ELL} />{' '}
            <StarsButton
              onClick={openSettingLimits}
              className={regulatoryButton}
              type="button"
              variant="text"
              translation={TXTCLI_Use_our_online_tools_and_play__ELL}
            />
          </div>
          <StarsButton
            onClick={openTOS}
            className={regulatoryButton}
            type="button"
            variant="text"
            translation={TXTWCL_Terms_of_Service}
          />
          {' | '}
          <StarsButton
            onClick={openWindow}
            className={regulatoryButton}
            type="button"
            variant="text"
            translation={TXTWCL_Responsible_gaming}
          />
        </Route>
      );
      break;
    case Sites.USMI:
      message = embedded && (
        <Route path={comFlows}>
          <div className={textContainerUs}>
            <Translation {...TXTWCL_XBet_with_your_headX_not_over__ELL} />
          </div>
          <div className={textContainerUs}>
            <Translation {...TXTWCL_Copyright_X_2001_2024_Rational_ELL} />
          </div>
          <div className={textContainerUs}>
            <Translation {...TXTWCL_TSG_Interactive_US_Services_Li_ELL} />{' '}
          </div>
          <div className={textContainerUs}>
            <Translation {...TXTWCL_It_is_a_Federal_offense_for_pe_ELL} />
          </div>
          <StarsButton
            onClick={openTOS}
            className={regulatoryButton}
            type="button"
            variant="text"
            translation={TXTMOB_Terms_of_Service_1}
          />
          {' | '}
          <StarsButton
            onClick={openPrivacy}
            className={regulatoryButton}
            type="button"
            variant="text"
            translation={TXTCLI_Privacy_Policy}
          />
          {' | '}
          <StarsButton
            onClick={openWindow}
            className={regulatoryButton}
            type="button"
            variant="text"
            translation={TXTWCL_Responsible_gaming}
          />
        </Route>
      );
  }

  return message ? <div className={wrapper}>{message}</div> : null;
};

export default RegulatoryInformation;
